import React from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CATEGORICAL_4 } from './palettes';

export interface WeeklySeries {
  key: string;
  label: string;
}

interface Props {
  title: string;
  subtitle?: string;
  series: WeeklySeries[];
  /** One row per week; `weekStart` is an ISO date and each series key a number or null. */
  data: Array<{ weekStart: string } & Record<string, number | string | null>>;
  /** Formats a y value for the axis and tooltip (e.g. a 0..1 share as a percent). */
  yFormat?: (value: number) => string;
  yDomain?: [number, number];
}

/** weekStart is a plain date; parse it as a local date so the axis never shifts a day. */
const formatWeek = (iso: string) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * A weeks-on-the-x-axis line chart in the Analytics charts' styling (card,
 * axes, grid, tooltip), without a period dropdown: the data already spans
 * every week there is. Used by the SEO admin AI Search tab
 * (Asana 1219288016488651).
 */
const WeeklyLineChart: React.FC<Props> = ({ title, subtitle, series, data, yFormat = (v) => String(v), yDomain }) => (
  <div className="chart-card" data-testid="weekly-line-chart">
    <div className="chart-header">
      <div className="chart-header-left">
        <h2>{title}</h2>
        {subtitle && <p className="chart-subtitle">{subtitle}</p>}
      </div>
      <div className="chart-header-right">
        <div className="chart-legend">
          {series.map((s, i) => (
            <React.Fragment key={s.key}>
              <span className="legend-dot" style={{ background: CATEGORICAL_4[i % CATEGORICAL_4.length] }} />
              <span className="legend-label">{s.label}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
    <div className="chart-container">
      {data.length === 0 ? (
        <div className="chart-error">No weeks recorded yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
            <XAxis
              dataKey="weekStart"
              tickFormatter={formatWeek}
              stroke="#666"
              tick={{ fill: '#999', fontSize: 12 }}
              axisLine={{ stroke: '#2a2a2a' }}
              tickLine={false}
            />
            <YAxis
              stroke="#666"
              tick={{ fill: '#999', fontSize: 12 }}
              axisLine={{ stroke: '#2a2a2a' }}
              tickLine={false}
              tickFormatter={yFormat}
              domain={yDomain}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', color: '#fff' }}
              labelFormatter={(label) => `Week of ${formatWeek(String(label))}`}
              formatter={(value, name) => [
                value === null || value === undefined ? 'n/a' : yFormat(Number(value)),
                series.find((s) => s.key === String(name))?.label ?? String(name),
              ]}
            />
            {series.map((s, i) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stroke={CATEGORICAL_4[i % CATEGORICAL_4.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  </div>
);

export default WeeklyLineChart;
